import { LifeBuoy } from 'lucide-react';
import { useState } from 'react';

import { notifyReportSent } from '@/features/support/composables/report';
import { useReportErrorMutation } from '@/features/support/services/supportService';

import type { SupportReportRequest } from '@/shared/api/types';

import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Textarea } from '@/shared/ui/textarea';

function composeMessage(note: string, detected: string): string {
  const trimmed = note.trim();
  return trimmed ? `${trimmed}\n\n---\nDetected: ${detected}` : detected;
}

export const SupportReportForm = ({
  message,
  note,
  onNoteChange,
  onSend,
  isPending,
}: {
  message: string;
  note: string;
  onNoteChange: (note: string) => void;
  onSend: () => void;
  isPending: boolean;
}) => (
  <>
    <DialogHeader>
      <DialogTitle>Report to support</DialogTitle>
      <DialogDescription>
        Tell us what you were doing and we&apos;ll take a look. The error details are attached
        automatically.
      </DialogDescription>
    </DialogHeader>

    <div className="grid gap-2.5">
      <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground wrap-break-word">
        {message}
      </p>
      <Textarea
        value={note}
        onChange={(e) => onNoteChange(e.target.value)}
        placeholder="What happened? Anything that helps us reproduce it (optional)."
        className="min-h-24"
      />
    </div>

    <DialogFooter className="gap-3 sm:gap-3">
      <DialogClose asChild>
        <Button variant="secondary" disabled={isPending}>
          Cancel
        </Button>
      </DialogClose>
      <Button icon={LifeBuoy} loading={isPending} onClick={onSend}>
        Send report
      </Button>
    </DialogFooter>
  </>
);

export const SupportReportDialog = ({
  open,
  onOpenChange,
  baseReport,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  baseReport: SupportReportRequest;
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md">
      {open && (
        <LiveSupportReportForm onClose={() => onOpenChange(false)} baseReport={baseReport} />
      )}
    </DialogContent>
  </Dialog>
);

const LiveSupportReportForm = ({
  onClose,
  baseReport,
}: {
  onClose: () => void;
  baseReport: SupportReportRequest;
}) => {
  const [note, setNote] = useState('');
  const { mutate, isPending } = useReportErrorMutation();

  const send = () => {
    mutate(
      { ...baseReport, message: composeMessage(note, baseReport.message) },
      {
        onSuccess: ({ reference }) => {
          notifyReportSent(reference);
          onClose();
        },
      }
    );
  };

  return (
    <SupportReportForm
      message={baseReport.message}
      note={note}
      onNoteChange={setNote}
      onSend={send}
      isPending={isPending}
    />
  );
};
